import React, { useState } from 'react';
import { 
  MessageCircle, 
  Clock, 
  PauseCircle, 
  Volume2, 
  ArrowDown, 
  ArrowUp, 
  Music, 
  Pause, 
  Play,
  Timer,
  AlertCircle
} from 'lucide-react';

/**
 * EmceeControls Component
 * 
 * Provides quick access to common emcee functions including:
 * - Custom announcements
 * - Timing controls
 * - Pre-defined messages
 */
const EmceeControls = ({ isDarkMode, onAnnounce, onTimingChange }) => {
  const [showCustomMessage, setShowCustomMessage] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownTime, setCountdownTime] = useState(5);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);

  // Common pre-defined announcements
  const quickMessages = [
    { label: "Welcome performers", message: "Please welcome our next performers to the stage!" },
    { label: "Judges ready", message: "Judges are ready. Performers, you may begin when ready." },
    { label: "Take positions", message: "Dancers, please take your positions." },
    { label: "Technical delay", message: "We're experiencing a brief technical delay. Please stand by." },
    { label: "5 min break", message: "We'll be taking a 5 minute break. Please return to your seats shortly." },
    { label: "Next division", message: "Coming up next is our junior division routines." },
  ];

  // Handle custom announcement
  const handleCustomAnnouncement = () => {
    if (customMessage.trim() && onAnnounce) {
      onAnnounce(customMessage.trim());
      setCustomMessage('');
      setShowCustomMessage(false);
    }
  };

  // Handle quick message selection
  const handleQuickMessage = (message) => {
    if (onAnnounce) {
      onAnnounce(message);
    }
  };

  // Handle countdown changes
  const handleCountdownChange = (minutes) => {
    if (onTimingChange) {
      onTimingChange({ type: 'countdown', minutes });
    }
    setShowCountdown(false);
  };

  // Toggle timer
  const toggleTimer = () => {
    setIsTimerRunning(!isTimerRunning);
    if (onTimingChange) {
      onTimingChange({ 
        type: 'timer', 
        action: !isTimerRunning ? 'start' : 'pause',
        seconds: timerSeconds
      });
    }
  };

  // Reset timer
  const resetTimer = () => {
    setIsTimerRunning(false);
    setTimerSeconds(0);
    if (onTimingChange) {
      onTimingChange({ type: 'timer', action: 'reset' });
    }
  };

  return (
    <div className={`rounded-lg ${
      isDarkMode ? 'bg-gray-800' : 'bg-white'
    } shadow-lg border ${
      isDarkMode ? 'border-gray-700' : 'border-gray-200'
    } p-4`}>
      <h3 className="text-lg font-semibold mb-4">Emcee Controls</h3>
      
      {/* Quick messages */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="h-4 w-4 text-blue-500" />
          <h4 className="font-medium">Quick Announcements</h4>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          {quickMessages.map((item, index) => (
            <button
              key={index}
              onClick={() => handleQuickMessage(item.message)}
              className={`px-3 py-2 text-sm text-left rounded-lg transition-all ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              {item.label}
            </button>
          ))}
          
          <button
            onClick={() => setShowCustomMessage(!showCustomMessage)}
            className={`px-3 py-2 text-sm text-left rounded-lg transition-all ${
              isDarkMode
                ? 'bg-blue-900/30 hover:bg-blue-800/40 text-blue-400'
                : 'bg-blue-100 hover:bg-blue-200 text-blue-600'
            }`}
          >
            Custom...
          </button>
        </div>
        
        {showCustomMessage && (
          <div className="mt-3 space-y-2">
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Type your custom announcement..."
              className={`w-full px-3 py-2 rounded-lg resize-none ${
                isDarkMode
                  ? 'bg-gray-700 text-white border-gray-600'
                  : 'bg-white text-gray-800 border-gray-300'
              } border`}
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCustomMessage(false)}
                className={`px-3 py-1 text-sm rounded-lg ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleCustomAnnouncement}
                className={`px-3 py-1 text-sm rounded-lg ${
                  isDarkMode
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-blue-500 hover:bg-blue-600'
                } text-white`}
                disabled={!customMessage.trim()}
              >
                Announce
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Timing controls */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-blue-500" />
          <h4 className="font-medium">Timing Controls</h4>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCountdown(!showCountdown)}
            className={`px-3 py-2 text-sm rounded-lg transition-all ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            <Timer className="h-4 w-4 inline mr-2" />
            Countdown
          </button>
          
          <button
            onClick={toggleTimer}
            className={`px-3 py-2 text-sm rounded-lg transition-all ${
              isTimerRunning
                ? isDarkMode 
                  ? 'bg-red-900/30 text-red-400'
                  : 'bg-red-100 text-red-600'
                : isDarkMode
                  ? 'bg-green-900/30 text-green-400'
                  : 'bg-green-100 text-green-600'
            }`}
          >
            {isTimerRunning ? (
              <>
                <Pause className="h-4 w-4 inline mr-2" />
                Pause Timer
              </>
            ) : (
              <>
                <Play className="h-4 w-4 inline mr-2" />
                Start Timer
              </>
            )}
          </button>
          
          {isTimerRunning && (
            <button
              onClick={resetTimer}
              className={`px-3 py-2 text-sm rounded-lg transition-all ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Reset
            </button>
          )}
          
          <button
            className={`px-3 py-2 text-sm rounded-lg transition-all ${
              isDarkMode
                ? 'bg-yellow-900/30 text-yellow-400'
                : 'bg-yellow-100 text-yellow-600'
            }`}
            onClick={() => handleQuickMessage("Five minute warning for the next performer!")}
          >
            <AlertCircle className="h-4 w-4 inline mr-2" />
            5 Min Warning
          </button>
        </div>
        
        {showCountdown && (
          <div className="mt-3 space-y-2">
            <div className="text-sm mb-2">Select countdown duration:</div>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 5, 10, 15, 20, 30].map((minutes) => (
                <button
                  key={minutes}
                  onClick={() => handleCountdownChange(minutes)}
                  className={`px-3 py-2 text-sm rounded-lg transition-all ${
                    isDarkMode
                      ? 'bg-gray-700 hover:bg-gray-600'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {minutes} min
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowCountdown(false)}
                className={`px-3 py-1 text-sm rounded-lg ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmceeControls; 